import {
  FileData,
  FileUploadStatus,
  FilesUploadResponse
} from 'src/app/projects/document-scan/drivers-integrations/drivers-integrations.types';
import {
  NotificationProgress,
  NotificationTaskEntry,
  NotificationTaskEntryStatus,
  NotificationTaskStatus
} from './notification-center.types';

const pendingStatuses = [
  FileUploadStatus.PENDING,
  FileUploadStatus.GETTING_URL,
  FileUploadStatus.UPLOADING
];

const successStatuses = [
  FileUploadStatus.UPLOADED,
  FileUploadStatus.UNCHANGED
];

export function isPendingUploadStatus(status: FileUploadStatus): boolean {
  return pendingStatuses.includes(status);
}

export function isSuccessfulUploadStatus(status: FileUploadStatus): boolean {
  return successStatuses.includes(status);
}

export function mapUploadEntryStatus(status: FileUploadStatus): NotificationTaskEntryStatus {
  if (isSuccessfulUploadStatus(status)) {
    return 'success';
  }

  if (isPendingUploadStatus(status)) {
    return status === FileUploadStatus.PENDING ? 'queued' : 'running';
  }

  return 'error';
}

export function getUploadErrorMessage(upload: FileData): string | undefined {
  switch (upload.status) {
    case FileUploadStatus.ERROR:
      return 'An unexpected error occurred during upload.';
    case FileUploadStatus.PROVIDER_ERROR:
      return `We couldn't retrieve the file from ${upload.provider}.`;
    case FileUploadStatus.NOT_SUPPORTED_TYPE:
      return 'This file type is not supported.';
    case FileUploadStatus.NOT_SUPPORTED_SIZE:
      return 'This file exceeds the allowed size.';
    case FileUploadStatus.MAX_COUNT_REACHED:
      return 'Maximum of 10 files allowed per upload.';
    default:
      return undefined;
  }
}

export function buildUploadEntries(files: FileData[]): NotificationTaskEntry[] {
  return files.map((file) => ({
    id: file.fileId,
    label: file.name,
    status: mapUploadEntryStatus(file.status),
    progress: file.progress,
    errorMessage: getUploadErrorMessage(file)
  }));
}

export function buildProgressFromEntries(entries: NotificationTaskEntry[]): NotificationProgress | undefined {
  if (!entries.length) {
    return {
      indeterminate: true
    };
  }

  const progressSum = entries.reduce((sum, entry) => sum + (entry.progress ?? 0), 0);
  const completed = entries.filter((entry) => entry.status === 'success').length;
  const failed = entries.filter((entry) => entry.status === 'error').length;
  const canceled = entries.filter((entry) => entry.status === 'canceled').length;

  return {
    percent: Math.round(progressSum / entries.length),
    current: completed + failed + canceled,
    total: entries.length,
    indeterminate: false
  };
}

export function buildTaskStatusFromEntries(entries: NotificationTaskEntry[]): NotificationTaskStatus {
  if (!entries.length) {
    return 'queued';
  }

  if (entries.some((entry) => entry.status === 'running' || entry.status === 'queued')) {
    return 'running';
  }

  if (entries.every((entry) => entry.status === 'canceled')) {
    return 'canceled';
  }

  if (entries.some((entry) => entry.status === 'error')) {
    return 'error';
  }

  return 'success';
}

export function buildUploadMessage(entries: NotificationTaskEntry[], noun: string): string {
  if (!entries.length) {
    return `Preparing ${noun} upload.`;
  }

  const total = entries.length;
  const successCount = entries.filter((entry) => entry.status === 'success').length;
  const errorCount = entries.filter((entry) => entry.status === 'error').length;
  const canceledCount = entries.filter((entry) => entry.status === 'canceled').length;

  if (entries.some((entry) => entry.status === 'running' || entry.status === 'queued')) {
    return `${successCount}/${total} ${noun} processed.`;
  }

  if (canceledCount === total) {
    return `${noun[0].toUpperCase()}${noun.slice(1)} upload canceled.`;
  }

  if (errorCount > 0) {
    return `${successCount}/${total} ${noun} uploaded successfully.`;
  }

  return `All ${noun} uploaded successfully.`;
}

export function buildResponseSummary(response: FilesUploadResponse): {
  successCount: number;
  errorCount: number;
  totalCount: number;
} {
  const totalCount = response.allFiles.length;
  const successCount = response.allFiles.filter((file) => isSuccessfulUploadStatus(file.status)).length;
  const errorCount = response.allFiles.filter(
    (file) => !isSuccessfulUploadStatus(file.status) && !isPendingUploadStatus(file.status)
  ).length;

  return {
    successCount,
    errorCount,
    totalCount
  };
}
